import {craftTimerSnapshot} from '../craft/time.js';
const MAX_REFRESH_MS=60_000;

export function liveTimerSnapshot({session,stageTarget=null,now=Date.now}={}){
  if(!session||typeof session!=='object')throw new TypeError('session is required');
  if(String(session.status||'')!=='smoking')throw new TypeError('Live timer requires a smoking session');
  // Preserve product validation labels and the persisted/public snapshot shape.
  if(!session.actualStartAt)throw new TypeError('actualStartAt is required');
  if(!session.activeStageStartedAt)throw new TypeError('activeStageStartedAt is required');
  let value;
  try{
    value=craftTimerSnapshot({startedAt:session.actualStartAt,activeStageId:session.activeStageId,stageStartedAt:session.activeStageStartedAt,plannedDurationMinutes:stageTarget?.plannedDurationMinutes,now});
  }catch(error){
    if(error instanceof TypeError)throw new TypeError(error.message.replaceAll('stageStartedAt','activeStageStartedAt').replaceAll('startedAt','actualStartAt'));
    throw error;
  }
  return Object.freeze({...value,source:Object.freeze({actualStartAt:value.source.startedAt,activeStageStartedAt:value.source.stageStartedAt,plannedDurationMinutes:value.source.plannedDurationMinutes})});
}

export function formatElapsedDuration(milliseconds){
  const totalSeconds=Math.max(0,Math.floor(Number(milliseconds||0)/1000));
  const days=Math.floor(totalSeconds/86400);
  const hours=Math.floor((totalSeconds%86400)/3600);
  const minutes=Math.floor((totalSeconds%3600)/60);
  const seconds=totalSeconds%60;
  const hh=String(hours).padStart(2,'0');
  const mm=String(minutes).padStart(2,'0');
  const ss=String(seconds).padStart(2,'0');
  return days?days+'d '+hh+':'+mm+':'+ss:hh+':'+mm+':'+ss;
}

export function createLiveTimerEngine({
  loadSession,
  loadStageTarget=async()=>null,
  now=Date.now,
  intervalMs=1000,
  scheduler=globalThis
}={}){
  if(typeof loadSession!=='function')throw new TypeError('loadSession is required');
  if(typeof loadStageTarget!=='function')throw new TypeError('loadStageTarget must be a function');
  const refresh=Number(intervalMs);
  if(!Number.isInteger(refresh)||refresh<250||refresh>MAX_REFRESH_MS){
    throw new TypeError('intervalMs must be between 250 and 60000');
  }

  async function snapshot(){
    const session=await loadSession();
    if(!session)throw new Error('SMOKING_SESSION_NOT_FOUND');
    const target=await loadStageTarget(session.activeStageId,session);
    return liveTimerSnapshot({session,stageTarget:target,now});
  }

  function subscribe(listener){
    if(typeof listener!=='function')throw new TypeError('listener is required');
    if(typeof scheduler?.setInterval!=='function'||typeof scheduler?.clearInterval!=='function'){
      throw new TypeError('scheduler must support setInterval/clearInterval');
    }
    let active=true;
    let running=false;

    async function emit(){
      if(!active||running)return;
      running=true;
      try{listener(await snapshot(),null);}
      catch(error){listener(null,error);}
      finally{running=false;}
    }

    void emit();
    const id=scheduler.setInterval(()=>{void emit();},refresh);
    return ()=>{
      if(!active)return;
      active=false;
      scheduler.clearInterval(id);
    };
  }

  function pause(){
    throw new Error('LIVE_TIMER_PAUSE_NOT_ENABLED');
  }

  function adjust(){
    throw new Error('LIVE_TIMER_ADJUST_NOT_ENABLED');
  }

  return Object.freeze({snapshot,subscribe,pause,adjust});
}
